import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { baseURL, discordAPI } from '../../services/apiClient';
import { useBusiness } from '../../services/BusinessContext';
import toast from 'react-hot-toast';
import { clearLocalStorageExcept } from '../../utils/storage';

// The sidebar docks beside the content at this width and up, and is an
// overlay below it. Keep in sync with the media queries in Sidebar.css.
const DESKTOP_QUERY = '(min-width: 1024px)';
const isDesktopWidth = () => window.matchMedia(DESKTOP_QUERY).matches;

const Sidebar = () => {
  const [isDesktop, setIsDesktop] = useState(isDesktopWidth);
  const [isOpen, setIsOpen] = useState(isDesktopWidth);
  const [expandedItems, setExpandedItems] = useState({}); // State for managing expanded submenus
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get user data and role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = localStorage.getItem('userRole');

  // State for business data when context is not available
  const [businessData, setBusinessData] = useState(null);
  const [businessDataLoading, setBusinessDataLoading] = useState(false);
  // State for pending quest approvals (super admin)
  const [questApprovalCount, setQuestApprovalCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // Get business context for AI points and permissions
  let businessContextData = null;
  try {
    businessContextData = useBusiness();
  } catch (error) {
    // Not in a business context, which is fine for non-business admin users
  }

  // If no business context but user is business admin, fetch business data directly
  useEffect(() => {
    if (!businessContextData && userRole === 'business_admin' && user.business_id && !businessDataLoading) {
      setBusinessDataLoading(true);
      
      const fetchBusinessData = async () => {
        try {
          const { businessAPI } = await import('../../services/apiClient');
          const response = await businessAPI.getDetails(user.business_id);
          setBusinessData(response.data);
        } catch (error) {
          console.error('[Sidebar] Failed to fetch business data:', error);
          setBusinessData(null);
        } finally {
          setBusinessDataLoading(false);
        }
      };
      
      fetchBusinessData();
    }
  }, [businessContextData, userRole, user.business_id, businessDataLoading]);

  // Fetch admin dashboard summary for super admins/admins
  useEffect(() => {
    if (userRole === 'super_admin' || userRole === 'admin') {
      const fetchSummary = async () => {
        try {
          const { adminDashboardAPI } = await import('../../services/apiClient');
          const response = await adminDashboardAPI.getSummary();
          const pendingApprovals = response?.data?.pending_quest_approvals ?? 0;
          const unread = response?.data?.unread_notifications ?? 0;
          setQuestApprovalCount(pendingApprovals);
          setUnreadNotificationCount(unread);
        } catch (error) {
          console.error('[Sidebar] Failed to fetch admin dashboard summary:', error);
        }
      };

      fetchSummary();
      const intervalId = setInterval(fetchSummary, 30000); // Refresh every 30s for responsiveness
      return () => clearInterval(intervalId);
    }
  }, [userRole]);

  // Use business context data if available, otherwise use directly fetched data
  const effectiveBusinessData = businessContextData || (businessData ? {
    business: businessData,
    aiPoints: businessData.ai_points || 0,
    permissions: businessData.permissions || {},
    hasPermission: (permission) => businessData.permissions?.[permission] === true,
    hasAIPoints: (points) => (businessData.ai_points || 0) >= points,
    questCredits: businessData.quest_credits_available || 0,
    questCreditsPurchased: businessData.quest_credits_purchased || 0,
    monthlyQuestLimit: businessData.monthly_quest_limit || 0,
    monthlyQuestsUsed: businessData.monthly_quests_used || 0,
    canCreateQuests: () => {
      if (userRole === 'super_admin') return true;
      
      const tierAllowsQuests = businessData.tier_info?.can_create_quests || false;
      // Check if tier allows quests OR if there's a legacy monthly quest limit
      const hasQuestCapability = tierAllowsQuests || (businessData.monthly_quest_limit > 0);
      
      if (!hasQuestCapability) {
        return (businessData.quest_credits_purchased || 0) > 0;
      }
      
      const monthlyLimit = businessData.monthly_quest_limit || 0;
      const monthlyUsed = businessData.monthly_quests_used || 0;
      const remainingMonthly = Math.max(0, monthlyLimit - monthlyUsed);
      const purchasedCredits = businessData.quest_credits_purchased || 0;
      
      return (remainingMonthly + purchasedCredits) > 0;
    },
    canAddAdminSeat: () => {
      const totalSeats = (businessData.tier_info?.admin_seat_limit || 1) + (businessData.admin_seats_purchased || 0);
      const usedSeats = businessData.current_admin_count || 0;
      return totalSeats === -1 || usedSeats < totalSeats; // -1 means unlimited
    },
    getAvailableQuestCredits: () => {
      if (userRole === 'super_admin') return Infinity;
      
      const tierAllowsQuests = businessData.tier_info?.can_create_quests || false;
      // Check if tier allows quests OR if there's a legacy monthly quest limit
      const hasQuestCapability = tierAllowsQuests || (businessData.monthly_quest_limit > 0);
      
      if (hasQuestCapability) {
        const monthlyLimit = businessData.monthly_quest_limit || 0;
        const monthlyUsed = businessData.monthly_quests_used || 0;
        const remainingMonthly = Math.max(0, monthlyLimit - monthlyUsed);
        const purchasedCredits = businessData.quest_credits_purchased || 0;
        
        return remainingMonthly + purchasedCredits;
      } else {
        return businessData.quest_credits_purchased || 0;
      }
    }
  } : null);

  // Extract business context from URL or user data
  const getBusinessContext = () => {
    // Check if we're in a business-specific route
    const businessMatch = location.pathname.match(/\/admin\/business\/(\d+)/);
    if (businessMatch) {
      return { businessId: businessMatch[1], fromUrl: true };
    }

    // For business admin, use their associated business
    if (userRole === 'business_admin' && user.business_id) {
      return { businessId: user.business_id, fromUser: true };
    }

    return null;
  };

  const businessContext = getBusinessContext();

  // Helper function to check if a feature is locked due to tier limitations
  const isFeatureLocked = (item) => {
    if (userRole === 'super_admin') return false; // Super admin bypass
    // Don't lock features while business data is still loading
    if (userRole === 'business_admin' && businessDataLoading) return false;
    // Don't lock features if no business context yet - allow navigation
    if (!effectiveBusinessData) return false;

    // Check for super business tier (usually highest tier with unlimited access)
    const isSuperTier = effectiveBusinessData.business?.tier === 'super' || 
                        effectiveBusinessData.business?.tier_info?.is_unlimited || 
                        false;
    
    if (isSuperTier) return false; // Super tier has all features

    // Check specific feature requirements
    if (item.requiresQuests) {
      return !effectiveBusinessData.canCreateQuests();
    }

    if (item.requiresAdminSeats) {
      return !effectiveBusinessData.canAddAdminSeat();
    }

    if (item.requiresAI) {
      return !effectiveBusinessData.hasAIPoints(item.minPoints || 1);
    }

    return false;
  };

  // Helper function to get redirect path for locked features
  const getRedirectPath = (item) => {
    if (item.requiresQuests) {
      // Check if they have any credits available before redirecting
      const credits = effectiveBusinessData.getAvailableQuestCredits();
      if (credits <= 0) {
        return '/business/purchase-quest-credits';
      }
      // If they have credits, don't redirect - allow the click to proceed
      return null;
    }
    if (item.requiresAdminSeats) {
      return '/business/purchase-admin-seats';
    }
    if (item.requiresAI) {
      return '/business/purchase-points';
    }
    return null;
  };

  // Enhanced click handler for sidebar items
  const handleSidebarClick = (item, originalPath) => {
    // Check if feature is locked
    if (isFeatureLocked(item)) {
      const redirectPath = getRedirectPath(item);
      if (redirectPath) {
        navigate(redirectPath);
        return;
      }
    }

    // Check permissions for regular items
    if (isFeatureDisabled(item)) {
      return; // Don't navigate if feature is disabled by permissions
    }

    // Normal navigation
    navigate(originalPath);
  };

  // Reset to the default for the new mode only when crossing the breakpoint
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const handleChange = (event) => {
      setIsDesktop(event.matches);
      setIsOpen(event.matches);
    };

    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // The overlay sidebar never stays open across navigations
  useEffect(() => {
    if (!isDesktopWidth()) {
      setIsOpen(false);
    }
  }, [location.pathname]);

  // Add/remove body class for sidebar spacing
  useEffect(() => {
    // Pages that should NOT have sidebar spacing (they have their own layout or custom sidebar)
    const excludedPages = [
      '/login', '/signup', '/businesses', // Public pages
      '/survey/', // Survey response pages (starts with)
      '/business/', // Public business pages (starts with) - but need to check if it's business admin context
    ];
    
    // Check if current page should be excluded
    const isExcludedPage = excludedPages.some(path => {
      if (path.endsWith('/')) {
        return location.pathname.startsWith(path);
      }
      return location.pathname === path;
    });

    // Special case: /business/:id routes should not have sidebar for public users, 
    // but business admin pages under /admin/business should have sidebar
    const isPublicBusinessPage = location.pathname.startsWith('/business/') && 
                                 !location.pathname.startsWith('/business/request') &&
                                 !location.pathname.startsWith('/business/subscription-management') &&
                                 !location.pathname.startsWith('/business/purchase-');

    // sidebar-active: content is shifted right to sit beside the docked sidebar.
    // has-sidebar: the floating toggle is on the page, so content clears it.
    const isDocked = !isExcludedPage && !isPublicBusinessPage && isDesktop && isOpen;
    document.body.classList.toggle('sidebar-active', isDocked);
    document.body.classList.add('has-sidebar');

    return () => {
      document.body.classList.remove('sidebar-active', 'has-sidebar');
    };
  }, [location.pathname, isDesktop, isOpen]);

  // Keep parent menu open if child route is active
  useEffect(() => {
    const currentPath = location.pathname;
    const navigationItems = getNavigationItems();
    
    navigationItems.forEach(item => {
      if (item.subItems) {
        const hasActiveChild = item.subItems.some(subItem => currentPath === subItem.path);
        if (hasActiveChild) {
          setExpandedItems(prev => ({ ...prev, [item.key]: true }));
        }
      }
    });
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');

      // Call the logout endpoint
      await fetch(`${baseURL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Clear session/authentication data but keep long-lived non-auth preferences
      // such as cookie consent so the banner does not reappear unnecessarily.
      clearLocalStorageExcept();

      // Redirect to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);

      // Even if the server request fails, still clear local storage and redirect
      clearLocalStorageExcept();
      navigate('/login');
    }
  };

  const toggleSidebar = () => {
    setIsOpen(open => !open);
  };

  // Function to toggle submenu expansion
  const toggleExpand = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Handle custom click items (like Discord Integration)
  const handleCustomClick = async (item) => {
    // Handle Discord Integration
    if (item.label === 'Discord Integration') {
      // Check if user is already linked to Discord
      if (user.discord_id) {
        // User is linked, navigate to audience management
        if (businessContext && businessContext.businessId) {
          navigate(`/admin/business/${businessContext.businessId}/audience`);
        } else {
          // Fallback if no business context
          navigate('/business-admin/dashboard');
        }
      } else {
        // User is not linked, initiate Discord linking
        try {
          const response = await discordAPI.initiateOAuth();
          if (response.data.redirect_url) {
            window.location.href = response.data.redirect_url;
          }
        } catch (error) {
          console.error('Error initiating Discord link:', error);
          toast.error("Could not initiate Discord linking. Please try again.");
        }
      }
    }

    // Close the overlay sidebar after click
    if (!isDesktop) {
      setIsOpen(false);
    }
  };

  // Generate navigation items based on context and role
  const getNavigationItems = () => {
    let items = [];

    // Business Admin context - Simplified to only show Surveys
    if (userRole === 'business_admin' && businessContext) {
      items = [
        {
          path: `/business-admin/dashboard`,
          label: 'Dashboard',
        },
        {
          label: 'Surveys',
          key: 'surveys',
          subItems: [
            {
              path: `/admin/business/${businessContext.businessId}/surveys/new`,
              label: 'New Survey',
              permissionKey: 'can_create_surveys'
            },            
            {
              path: '/survey-builder',
              label: 'AI Survey Builder',
              requiresAI: true,
              minPoints: 1
            },
            {
              path: `/admin/business/${businessContext.businessId}/surveys/manage`,
              label: 'Manage Surveys',
              permissionKey: 'can_edit_surveys'
            }
          ]
        },
      ];
    }

    // Super Admin general context
    else if (userRole === 'super_admin' || userRole === 'admin') {
      const questLabel = 'Quests';
      const questApprovalsSubLabel = 'Quest Approvals';

      items = [
        {
          label: 'Surveys',
          icon: 'ri-file-list-3-line',
          key: 'surveys',
          subItems: [
            {
              path: '/create-survey',
              icon: 'ri-corner-down-right-line',
              label: 'New Survey',
            },
            {
              path: '/savedsurveys',
              icon: 'ri-corner-down-right-line',
              label: 'All Surveys',
            },
            {
              path: '/admin/surveys/deleted',
              icon: 'ri-corner-down-right-line',
              label: 'Deleted Surveys',
            }
          ]
        },
        {
          label: 'Business',
          icon: 'ri-building-line',
          key: 'business',
          subItems: [
            {
              path: '/admin/business/manage',
              icon: 'ri-corner-down-right-line',
              label: 'Manage Business',
            },
            {
              path: '/admin/business/new',
              icon: 'ri-corner-down-right-line',
              label: 'Add Business',
            }
          ]
        },
        {
          label: 'AI Tools',
          icon: 'ri-robot-line',
          key: 'ai',
          subItems: [
            {
              path: '/admin/ai-analytics',
              icon: 'ri-corner-down-right-line',
              label: 'AI Analytics',
            },
            {
              path: '/survey-builder',
              icon: 'ri-corner-down-right-line',
              label: 'AI Survey Builder',
            }
          ]
        },

        {
          path: '/admin/users/manage',
          icon: 'ri-group-line',
          label: 'Users',
        },
        {
          path: '/admin/audit-logs',
          icon: 'ri-file-list-2-line',
          label: 'Audit Logs',
        },
        ...(userRole === 'super_admin' ? [{
          path: '/admin/settings',
          icon: 'ri-shield-user-line',
          label: 'Admin Settings',
        }] : []),
        { type: 'separator' },
        {
          path: '/admin',
          icon: 'ri-admin-line',
          label: 'Super Admin Panel',
          priority: true
        },
      ];
    }

    // Regular User Navigation
    else if (userRole === 'user') {
      items = [
        {
          path: '/user/home',
          icon: 'ri-home-4-line',
          label: 'Homepage',
          description: 'Your personal dashboard'
        },
        {
          path: '/surveys',
          icon: 'ri-file-list-3-line',
          label: 'Surveys',
          description: 'Find surveys from businesses'
        },
        {
          path: '/quests',
          icon: 'ri-compass-3-line',
          label: 'Quests',
          description: 'Discover quests from businesses'
        },
        {
          path: '/businesses',
          icon: 'ri-building-line',
          label: 'Businesses',
          description: 'Explore all businesses'
        },
        {
          path: '/marketplace',
          icon: 'ri-store-2-line',
          label: 'Marketplace',
          description: 'Redeem your XP for rewards'
        },
        {
          path: '/user/xp-history',
          icon: 'ri-history-line',
          label: 'XP History',
          description: 'View your XP transactions'
        },
        { type: 'separator' },
        {
          path: '/business/request/new',
          icon: 'ri-bug-line',
          label: 'Report Bugs',
          description: 'Report issues you found'
        },
        {
          path: '/business/request/new',
          icon: 'ri-lightbulb-flash-line',
          label: 'Request Features',
          description: 'Suggest new features'
        }
      ];
    }

    return items;
  };

  const navigationItems = getNavigationItems();

  // Helper function to check if an item should be disabled due to permissions
  const isFeatureDisabled = (item) => {
    if (!item.permissionKey && !item.permissionKeys) return false;
    if (userRole === 'super_admin') return false; // super admin bypass
    // Don't disable features while business data is still loading
    if (userRole === 'business_admin' && businessDataLoading) return false;
    if (!effectiveBusinessData) return true;
    if (item.permissionKey) {
      return !effectiveBusinessData.hasPermission(item.permissionKey);
    }
    if (item.permissionKeys) {
      // disable only if NONE of the listed permissions are allowed
      const allowed = item.permissionKeys.some(pk => effectiveBusinessData.hasPermission(pk));
      return !allowed;
    }
    return false;
  };

  // Helper function to check if an AI feature should be disabled
  const isAIFeatureDisabled = (item) => {
    if (!item.requiresAI) return false;
    if (userRole === 'super_admin') return false; // Super admin bypass
    // Don't disable AI features while business data is still loading
    if (userRole === 'business_admin' && businessDataLoading) return false;

    if (!effectiveBusinessData) {
      return true; // No business context
    }

    // Check permission
    if (item.aiPermission && !effectiveBusinessData.hasPermission(item.aiPermission)) {
      return true;
    }

    // For business admin, if they don't have AI points, redirect them to purchase
    if (item.minPoints && !effectiveBusinessData.hasAIPoints(item.minPoints)) {
      return true; // This will show as locked and redirect on click
    }
    return false;
  };

  // Get sidebar title based on context
  const getSidebarTitle = () => {
    if (businessContext && userRole === 'business_admin') {
      return 'Business Panel';
    }
    if (businessContext && (userRole === 'super_admin' || userRole === 'admin')) {
      return `Admin: Business ${businessContext.businessId}`;
    }
    if (userRole === 'super_admin' || userRole === 'admin') {
      return 'Super Admin';
    }
    return 'Eclipseer';
  };

  return (
    <>
      <button
        className={`sidebar-toggle ${isOpen ? 'open' : ''}`}
        onClick={toggleSidebar}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
      >
        <i className={`ri-${isOpen ? 'close' : 'menu'}-line`}></i>
      </button>

      <div className={`sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={toggleSidebar}></div>

      <div className={`common-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-top-icons">
          <button className="icon-button" onClick={toggleSidebar} aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'} title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            <i className={`ri-${isOpen ? 'arrow-left-s-line' : 'menu-2-line'}`}></i>
          </button>
        </div>

        <div className="sidebar-content">
          <nav className="sidebar-nav">
            {navigationItems.map((item, index) => {
              // Handle separator
              if (item.type === 'separator') {
                return <div key={`separator-${index}`} className="sidebar-separator" />;
              }

              // Handle items with subitems
              if (item.subItems) {
                const isExpanded = expandedItems[item.key];
                const hasActiveChild = item.subItems.some(subItem => location.pathname === subItem.path);
                
                return (
                  <div key={index} className="nav-item-container">
                    <div 
                      className={`sidebar-link ${hasActiveChild ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleExpand(item.key)}
                      style={{ fontWeight: item.bold ? 'bold' : 'normal' }}
                    >
                      <i className={`ri-arrow-right-s-line expand-icon ${isExpanded ? 'expanded' : ''}`}></i>
                      {item.icon && userRole !== 'business_admin' && <i className={item.icon}></i>}
                      <span>{item.label}</span>
                      {item.count > 0 && (
                        <span className="badge-count">{item.count}</span>
                      )}
                    </div>
                    <div className={`submenu ${isExpanded ? 'expanded' : ''}`}>
                      {item.subItems.map((subItem, subIndex) => {
                        const isDisabled = isFeatureDisabled(subItem) || isAIFeatureDisabled(subItem);
                        const isLocked = isFeatureLocked(subItem);
                        
                        if (isDisabled || isLocked) {
                          let tooltipText = '';
                                                     if (isLocked) {
                             if (subItem.requiresQuests) {
                               const monthlyLimit = effectiveBusinessData?.monthlyQuestLimit || 0;
                               const monthlyUsed = effectiveBusinessData?.monthlyQuestsUsed || 0;
                               const remainingMonthly = Math.max(0, monthlyLimit - monthlyUsed);
                               const purchased = effectiveBusinessData?.questCreditsPurchased || 0;
                               
                               if (remainingMonthly > 0) {
                                 tooltipText = `Requires quest credits - you have ${remainingMonthly} monthly quests + ${purchased} purchased credits remaining`;
                               } else {
                                 tooltipText = 'Requires quest credits - click to purchase quest packages';
                               }
                             } else if (subItem.requiresAdminSeats) {
                               tooltipText = 'Requires admin seats - click to purchase admin seat packages';
                             } else if (subItem.requiresAI) {
                               tooltipText = 'Requires AI points - click to purchase AI points';
                             }
                           } else {
                            tooltipText = `Requires ${subItem.minPoints || 1} AI points and ${subItem.aiPermission || 'AI'} permission`;
                          }

                          return (
                            <div
                              key={subIndex}
                              className={`sidebar-link submenu-link ${isLocked ? 'locked' : 'disabled'}`}
                              title={tooltipText}
                              onClick={() => isLocked && handleSidebarClick(subItem, subItem.path)}
                              style={{ cursor: isLocked ? 'pointer' : 'not-allowed' }}
                            >
                              {subItem.icon && userRole !== 'business_admin' && <i className={subItem.icon}></i>}
                              <span>{subItem.label}</span>
                              {subItem.count > 0 && (
                                <span className="badge-count">{subItem.count}</span>
                              )}
                              <i className={`ri-${isLocked ? 'shopping-cart' : 'lock'}-line lock-icon`}></i>
                            </div>
                          );
                        }

                        return (
                          <NavLink
                            key={subIndex}
                            to={subItem.path}
                            className={({ isActive }) => `sidebar-link submenu-link ${isActive ? 'active' : ''}`}
                            onClick={() => !isDesktop && setIsOpen(false)}
                            style={{ fontWeight: subItem.bold ? 'bold' : 'normal' }}
                          >
                            {subItem.icon && userRole !== 'business_admin' && <i className={subItem.icon}></i>}
                            <span>{subItem.label}</span>
                            {subItem.count > 0 && (
                              <span className="badge-count">{subItem.count}</span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Handle regular navigation items
              const regDisabled = isFeatureDisabled(item) || isAIFeatureDisabled(item);
              const regLocked = isFeatureLocked(item);

              if (regDisabled || regLocked) {
                let tooltipText = '';
                                 if (regLocked) {
                   if (item.requiresQuests) {
                     const monthlyLimit = effectiveBusinessData?.monthlyQuestLimit || 0;
                     const monthlyUsed = effectiveBusinessData?.monthlyQuestsUsed || 0;
                     const remainingMonthly = Math.max(0, monthlyLimit - monthlyUsed);
                     const purchased = effectiveBusinessData?.questCreditsPurchased || 0;

                     if (remainingMonthly > 0) {
                       tooltipText = `Requires quest credits - you have ${remainingMonthly} monthly quests + ${purchased} purchased credits remaining`;
                     } else {
                       tooltipText = 'Requires quest credits - click to purchase quest packages';
                     }
                   } else if (item.requiresAdminSeats) {
                     tooltipText = 'Requires admin seats - click to purchase admin seat packages';
                   } else if (item.requiresAI) {
                     tooltipText = 'Requires AI points - click to purchase AI points';
                   }
                 } else {
                  tooltipText = 'Feature disabled';
                }

                return (
                  <div
                    key={index}
                    className={`sidebar-link ${regLocked ? 'locked' : 'disabled'}`}
                    title={tooltipText}
                    onClick={() => regLocked && handleSidebarClick(item, item.path)}
                    style={{ cursor: regLocked ? 'pointer' : 'not-allowed' }}
                  >
                    {item.icon && userRole !== 'business_admin' && <i className={item.icon}></i>}
                    <span>{item.label}</span>
                    {item.count > 0 && (
                      <span className="badge-count">{item.count}</span>
                    )}
                    <i className={`ri-${regLocked ? 'shopping-cart' : 'lock'}-line lock-icon`}></i>
                  </div>
                );
              }

              // Handle custom click items (like Discord Integration)
              if (item.customClick) {
                return (
                  <div
                    key={index}
                    className="sidebar-link"
                    onClick={() => handleCustomClick(item)}
                    style={{ cursor: 'pointer' }}
                  >
                    {item.icon && userRole !== 'business_admin' && <i className={item.icon}></i>}
                    <span>{item.label}</span>
                    {item.count > 0 && (
                      <span className="badge-count">{item.count}</span>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={index}
                  to={item.path}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''} ${item.priority ? 'priority' : ''}`}
                  style={{ fontWeight: item.bold ? 'bold' : 'normal' }}
                  onClick={() => !isDesktop && setIsOpen(false)}
                >
                  {item.icon && userRole !== 'business_admin' && <i className={item.icon}></i>}
                  <span>{item.label}</span>
                  {item.count > 0 && (
                    <span className="badge-count">{item.count}</span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="user-profile-section">
            {showUserMenu && (
              <div className="user-menu">
                <button onClick={() => { navigate('/user/update-password'); setShowUserMenu(false); }} className="logout-button">
                  <i className="ri-lock-password-line"></i>
                  <span>Update Password</span>
                </button>
                <button onClick={handleLogout} className="logout-button">
                  <i className="ri-logout-box-line"></i>
                  <span>Logout</span>
                </button>
              </div>
            )}
            <div className="user-profile" onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="user-avatar1">
                <i className="ri-user-line"></i>
              </div>
              <div className="user-info">
                <span className="user-name1">{user.name || 'User'}</span>
                <span className="user-role">{userRole}</span>
              </div>
              <i className={`ri-arrow-down-s-line ${showUserMenu ? 'expanded' : ''}`}></i>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
